/**
 * SSE frame parsing for the streaming chat endpoint.
 *
 * Extracted from useConversationChat.ts so it can be unit-tested in
 * isolation from the fetch/ReadableStream plumbing. Behavior-preserving
 * extraction plus several hardening fixes:
 *
 * - Frames are reassembled across `reader.read()` chunk boundaries instead
 *   of being parsed line-by-line per chunk (a `data: ...` frame split across
 *   two TCP chunks used to produce a swallowed JSON.parse error).
 * - `\r\n` line endings are normalized to `\n` before splitting into frames,
 *   since an intermediary proxy may rewrite line endings in transit.
 * - A streamed `error` event surfaces as `SseStreamError` in the return
 *   value's `error` field instead of being caught by the same try/catch that
 *   guards against malformed JSON. Parsing stops at the error frame, but
 *   events parsed *before* it (possibly coalesced into the same network
 *   chunk) are still returned via `events` so the caller can apply them
 *   before throwing -- otherwise content already generated in the same
 *   buffer as a trailing SAVE_FAILED/QUOTA error frame would be silently
 *   dropped.
 */

import { ChatSource } from '../../api/chatTypes';
import { log } from '../../utils/errors/logger';

// --- Types ---

/**
 * Discriminated union of all SSE event shapes emitted by the streaming chat endpoint.
 *
 * Backwards-compatible: the legacy `context` event (kill-switch OFF) is kept alongside
 * the new `sources_filtered` event (kill-switch ON, citation-gating active).
 *
 * `gated` is emitted by `build_gated_stream_events` (backend
 * `services/rag/citation_policy.py`) as the second event in the sequence
 * `context -> gated -> content -> done` whenever citation-gating blocks
 * generation for the turn (no usable retrieved context).
 *
 * `tool_call` / `tool_result` / `tool_steps_discarded` mirror
 * `backend/api/services/rag/stream_events.py` (`ToolCallStreamEvent` /
 * `ToolResultStreamEvent` / `ToolStepsDiscardedEvent`) field-for-field.
 * `arguments` and `summary` are already sanitized and length-capped
 * server-side (S11) -- render them as plain text only, never through the
 * markdown renderer. `tool_steps_discarded` is not yet emitted by any
 * backend code path (wired in a later package's router error path) but is
 * modeled and parsed here defensively so the frontend does not need a
 * follow-up change once it is.
 */
export type SseEvent =
  | { type: 'context'; context: ChatSource[] }
  | { type: 'content'; content: string }
  | { type: 'answer_final'; content: string }
  | { type: 'sources_filtered'; context: ChatSource[] }
  | { type: 'gated'; message?: string }
  | { type: 'done'; message?: string }
  | { type: 'error'; error?: string; status?: number; code?: string }
  | {
      type: 'tool_call';
      call_id: string;
      server_key: string;
      tool_name: string;
      arguments: string;
    }
  | {
      type: 'tool_result';
      call_id: string;
      server_key: string;
      tool_name: string;
      summary: string;
      status: 'succeeded' | 'failed' | 'refused';
    }
  | { type: 'tool_steps_discarded' };

/** Error raised when the stream itself emits a `type: 'error'` SSE event. */
export class SseStreamError extends Error {
  readonly status?: number;
  readonly code?: string;

  constructor(message: string, options?: { status?: number; code?: string }) {
    super(message);
    this.name = 'SseStreamError';
    this.status = options?.status;
    this.code = options?.code;
  }
}

// SSE frames are separated by a blank line.
const FRAME_BOUNDARY = '\n\n';

/**
 * Upper bound on how large the reassembly buffer (accumulated `remainder` +
 * newly read chunks) is allowed to grow between complete frames. Guards
 * against unbounded memory growth if the server streams a malformed or
 * never-terminated frame (missing `\n\n` boundary) -- without a cap, the
 * hook's read loop would keep concatenating chunks into `buffer` forever.
 */
export const MAX_SSE_BUFFER = 1_048_576; // 1 MiB

/** Thrown by `assertBufferWithinLimit` when the SSE reassembly buffer exceeds `MAX_SSE_BUFFER`. */
export class SseBufferOverflowError extends Error {
  constructor(size: number, limit: number) {
    super(
      `SSE reassembly buffer exceeded ${limit} bytes (got ${size}); aborting stream to avoid unbounded memory growth.`,
    );
    this.name = 'SseBufferOverflowError';
  }
}

/**
 * Throws `SseBufferOverflowError` if `buffer` exceeds `limit` characters.
 * Intended to be called after each `reader.read()` chunk is folded into the
 * buffer, so a stream that never produces a frame boundary (`\n\n`) cannot
 * grow the buffer without bound.
 */
export function assertBufferWithinLimit(buffer: string, limit: number = MAX_SSE_BUFFER): void {
  if (buffer.length > limit) {
    throw new SseBufferOverflowError(buffer.length, limit);
  }
}

/**
 * Parse a single SSE frame (the text between two `\n\n` boundaries) into zero
 * or more events. Only `data: ` lines are interpreted, consistent with the
 * previous inline implementation; other SSE fields (`event:`, `id:`, comments)
 * are ignored.
 *
 * Malformed JSON on a `data: ` line is logged and skipped (does not throw) so
 * one bad frame cannot abort the whole stream. A well-formed `type: 'error'`
 * event stops parsing of this frame (any further lines in the same frame are
 * not processed) and is returned via `error` rather than thrown, so the
 * events already collected -- from this frame or earlier frames in the same
 * buffer -- are not lost.
 */
function parseSseFrame(frame: string): { events: SseEvent[]; error?: SseStreamError } {
  const events: SseEvent[] = [];

  for (const line of frame.split('\n')) {
    if (!line.startsWith('data: ')) {
      continue;
    }

    let data: SseEvent;
    try {
      data = JSON.parse(line.slice(6)) as SseEvent;
    } catch (parseError) {
      log.warn('Failed to parse SSE data', {
        // Truncated: a malformed line could in principle carry a large
        // payload (e.g. a mis-encoded content chunk) -- avoid logging it in
        // full.
        line: line.slice(0, 80),
        error: parseError instanceof Error ? parseError.message : String(parseError),
      });
      continue;
    }

    if (data.type === 'error') {
      const error = new SseStreamError(data.error ?? 'Streaming error occurred', {
        status: data.status,
        code: data.code,
      });
      return { events, error };
    }

    events.push(data);
  }

  return { events };
}

/**
 * Parse as many complete SSE frames as are present in `buffer`, returning the
 * parsed events plus whatever incomplete trailing text remains (to be
 * prepended to the next chunk before parsing again).
 *
 * Pass `flush: true` once the underlying stream has ended so any leftover
 * (boundary-less) text still in the buffer is parsed as a final frame rather
 * than silently dropped.
 *
 * If a frame contains a `type: 'error'` event, parsing stops at that frame
 * (any frames after it in the same buffer are not parsed) and the error is
 * returned via `error` -- it is NOT thrown. This lets a caller apply the
 * `events` that were coalesced into the same network chunk *before* the
 * error (e.g. trailing `content` chunks that arrived alongside a
 * SAVE_FAILED/QUOTA error frame) rather than dropping them.
 */
export function parseSseFrames(
  buffer: string,
  options: { flush?: boolean } = {},
): { events: SseEvent[]; remainder: string; error?: SseStreamError } {
  // Normalize line endings: an intermediary proxy may rewrite `\n` to `\r\n`.
  const normalized = buffer.replace(/\r\n/g, '\n');

  const parts = normalized.split(FRAME_BOUNDARY);
  let remainder = parts.pop() ?? '';
  const frames = parts;

  if (options.flush && remainder.trim().length > 0) {
    frames.push(remainder);
    remainder = '';
  }

  const events: SseEvent[] = [];
  for (const frame of frames) {
    const result = parseSseFrame(frame);
    events.push(...result.events);
    if (result.error) {
      return { events, remainder: '', error: result.error };
    }
  }

  return { events, remainder };
}
